import { useCallback, useEffect, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import {
  getCrmOrderActivities,
  getCrmLeadActivities,
  postCrmOrderActivity,
  postCrmLeadActivity,
  patchCrmProductionNotes,
  type CrmActivity,
} from '../../../api';
import { CRM_ACTIVITY_KIND_LABELS, CRM_ACTIVITY_TEMPLATES } from './crmActivityLabels';
import { useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { isProductionUiSandbox } from '../../../adminAccess';
import type { CrmAccessParams } from '../../../crmAccess';
import { canCrmFullControl, resolveCrmScope } from '../../../crmAccess';

type Props = {
  orderId?: number;
  leadId?: number;
  access: CrmAccessParams;
  productionMode?: boolean;
  hapticSelection: () => void;
};

export function CrmActivityFeed({ orderId, leadId, access, productionMode, hapticSelection }: Props) {
  const [byDate, setByDate] = useState<Record<string, CrmActivity[]>>({});
  const [productionNotes, setProductionNotes] = useState('');
  const [text, setText] = useState('');
  const [kind, setKind] = useState(productionMode ? 'production_note' : 'call');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const invalidate = useInvalidateCrm();

  const scope = resolveCrmScope(access);
  const isProd = productionMode || scope === 'production_read';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (leadId) {
        const data = await getCrmLeadActivities(leadId);
        setByDate(data.by_date || {});
        setProductionNotes('');
      } else if (orderId) {
        const data = await getCrmOrderActivities(orderId);
        setByDate(data.by_date || {});
        setProductionNotes(data.production_notes || '');
      }
    } finally {
      setLoading(false);
    }
  }, [orderId, leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    hapticSelection();
    setSaving(true);
    try {
      if (leadId) {
        await postCrmLeadActivity(leadId, { body, kind });
      } else if (orderId) {
        await postCrmOrderActivity(orderId, {
          body,
          kind,
          visibility: kind === 'handoff_to_production' || kind === 'production_note' ? 'all_staff' : undefined,
        });
      }
      setText('');
      await load();
      void invalidate();
    } finally {
      setSaving(false);
    }
  };

  const saveProdNotes = async () => {
    if (!orderId || (!isProd && !canCrmFullControl(access))) return;
    await patchCrmProductionNotes(orderId, productionNotes.trim() || null);
    hapticSelection();
  };

  const kindOptions = isProd
    ? ['production_note', 'handoff_to_production']
    : ['call', 'note', 'meeting', 'handoff_to_production'];

  return (
    <div className="space-y-3">
      {loading && <p className="text-xs text-center py-4" style={{ color: 'var(--text-3)' }}>Загрузка…</p>}
      {!loading && Object.keys(byDate).length === 0 && (
        <p className="text-xs text-center py-3" style={{ color: 'var(--text-3)' }}>
          Пока нет записей. Добавьте итог звонка или встречи.
        </p>
      )}
      {Object.entries(byDate).map(([day, items]) => (
        <div
          key={day}
          className="rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
        >
          <div
            className="flex items-center justify-between px-3 py-2 border-b"
            style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(99,179,237,0.06)' }}
          >
            <p className="text-[11px] font-bold" style={{ color: 'var(--cyan-bright)' }}>
              {new Date(day).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-3)' }}
            >
              {items.length}
            </span>
          </div>
          <div className="px-2 py-2 space-y-1.5">
            {items.map((a) => (
              <ActivityRow key={`${a.id}-${a.created_at}`} activity={a} />
            ))}
          </div>
        </div>
      ))}

      {(isProd || !isProductionUiSandbox({ role: access.role, telegramId: access.telegramId })) && (
        <div className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
          <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
            {isProd ? 'Заметка для цеха' : 'Запись за сегодня'}
          </p>
          <select className="input-glass w-full text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
            {kindOptions.map((k) => (
              <option key={k} value={k}>
                {CRM_ACTIVITY_KIND_LABELS[k] || k}
              </option>
            ))}
          </select>
          {!isProd && (
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
              {CRM_ACTIVITY_TEMPLATES.map((tpl) => (
                <button
                  key={tpl}
                  type="button"
                  onClick={() => setText(tpl)}
                  className="press shrink-0 px-2 py-1 rounded-lg text-[10px] font-semibold touch-manipulation"
                  style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}
                >
                  {tpl}
                </button>
              ))}
            </div>
          )}
          <textarea
            className="input-glass w-full text-sm min-h-[72px] resize-none touch-manipulation"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isProd ? 'Что передать производству…' : 'Итог разговора с клиентом…'}
          />
          <button
            type="button"
            disabled={saving || !text.trim()}
            onClick={() => void submit()}
            className="press w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold"
            style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
          >
            <MessageSquarePlus size={14} /> {saving ? '…' : 'Сохранить'}
          </button>
        </div>
      )}

      {orderId && (isProd || canCrmFullControl(access)) && (
        <div className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(159,122,234,0.08)', border: '1px solid rgba(159,122,234,0.25)' }}>
          <p className="text-[10px] font-bold uppercase" style={{ color: '#B794F4' }}>Производство (сводка)</p>
          <textarea
            className="input-glass w-full text-sm min-h-[56px] resize-none"
            value={productionNotes}
            onChange={(e) => setProductionNotes(e.target.value)}
            placeholder="Технические детали для цеха…"
          />
          <button
            type="button"
            onClick={() => void saveProdNotes()}
            className="press text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(159,122,234,0.2)', color: '#D6BCFA' }}
          >
            Сохранить сводку
          </button>
        </div>
      )}
    </div>
  );
}

function ActivityRow({ activity }: { activity: CrmActivity }) {
  const kindLabel = CRM_ACTIVITY_KIND_LABELS[activity.kind] || activity.kind;
  const vis = activity.visibility === 'all_staff';
  return (
    <div
      className="p-3 rounded-xl text-xs"
      style={{
        background: activity.is_legacy ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${activity.is_legacy ? 'var(--border-subtle)' : 'rgba(99,179,237,0.15)'}`,
      }}
    >
      <div className="flex justify-between gap-2 mb-1">
        <span className="font-bold" style={{ color: 'var(--cyan)' }}>{kindLabel}</span>
        {vis && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(159,122,234,0.15)', color: '#B794F4' }}>
            цех
          </span>
        )}
      </div>
      <p className="whitespace-pre-wrap break-words" style={{ color: 'var(--text-2)' }}>{activity.body}</p>
      <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-3)' }}>
        {activity.author_name || '—'}
        {activity.is_legacy ? ' · архив' : ''}
      </p>
    </div>
  );
}
